/**
=========================================================
* Public Registration Demo Component
=========================================================
* 
* Demo component to test the public registration form
* with sample data and mock API responses
*/

import { useState } from "react";
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  Grid,
} from "@mui/material";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Icons
import SportsCricketIcon from "@mui/icons-material/SportsCricket";
import LaunchIcon from "@mui/icons-material/Launch";

function PublicRegistrationDemo() {
  const [registrationCode, setRegistrationCode] = useState("DEMO123");
  const [showDemo, setShowDemo] = useState(false);

  const handleLaunchDemo = () => {
    if (registrationCode.trim()) {
      const url = `/public-registration?public_registration_code=${encodeURIComponent(registrationCode.trim())}`;
      window.open(url, '_blank');
    }
  };

  const sampleCodes = [
    { code: "DEMO123", description: "Demo Registration Code" },
    { code: "SUMMER2024", description: "Summer Cricket League" },
    { code: "CRICKET123", description: "Test Tournament" },
  ];

  return (
    <SoftBox
      minHeight="100vh"
      bgColor="grey-100"
      py={4}
    >
      <Container maxWidth="md">
        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            <Box textAlign="center" mb={4}>
              <SportsCricketIcon 
                sx={{ 
                  fontSize: 80, 
                  color: "primary.main", 
                  mb: 2 
                }} 
              />
              <SoftTypography variant="h3" color="dark" fontWeight="bold" mb={2}>
                Public Registration Demo
              </SoftTypography>
              <SoftTypography variant="body1" color="text" mb={3}>
                Test the public registration form with sample data
              </SoftTypography>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Note:</strong> This is a demo component. The actual registration form 
              will load series details from the API based on the registration code you provide.
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <SoftTypography variant="h6" color="dark" fontWeight="bold" mb={2}>
                  Enter Registration Code
                </SoftTypography>
                <TextField
                  fullWidth
                  label="Public Registration Code"
                  value={registrationCode}
                  onChange={(e) => setRegistrationCode(e.target.value)}
                  placeholder="e.g., DEMO123, SUMMER2024"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <SoftButton
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleLaunchDemo}
                  startIcon={<LaunchIcon />}
                  disabled={!registrationCode.trim()}
                >
                  Launch Registration Form
                </SoftButton>
              </Grid>

              <Grid item xs={12}>
                <SoftTypography variant="h6" color="dark" fontWeight="bold" mb={2}>
                  Sample Registration Codes
                </SoftTypography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {sampleCodes.map((sample, index) => (
                    <Button
                      key={index}
                      variant="outlined"
                      size="small"
                      onClick={() => setRegistrationCode(sample.code)}
                      sx={{ mb: 1 }}
                    >
                      {sample.code}
                    </Button>
                  ))}
                </Box>
                <SoftTypography variant="body2" color="text.secondary" mt={1}>
                  Click any code above to use it in the form
                </SoftTypography>
              </Grid>

              <Grid item xs={12}>
                <SoftTypography variant="h6" color="dark" fontWeight="bold" mb={2}>
                  Features to Test
                </SoftTypography>
                <Box component="ul" sx={{ pl: 2 }}>
                  <SoftTypography component="li" variant="body2" color="text" mb={1}>
                    Form validation and error handling
                  </SoftTypography>
                  <SoftTypography component="li" variant="body2" color="text" mb={1}>
                    Local storage and quick fill functionality
                  </SoftTypography>
                  <SoftTypography component="li" variant="body2" color="text" mb={1}>
                    File upload for profile pictures and payment screenshots
                  </SoftTypography>
                  <SoftTypography component="li" variant="body2" color="text" mb={1}>
                    Responsive design on different screen sizes
                  </SoftTypography>
                  <SoftTypography component="li" variant="body2" color="text" mb={1}>
                    Dynamic form fields based on series settings
                  </SoftTypography>
                  <SoftTypography component="li" variant="body2" color="text" mb={1}>
                    Success and error state handling
                  </SoftTypography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <strong>Important:</strong> This demo uses mock data. In production, 
                  the form will connect to your backend API to load real series details 
                  and submit actual registrations.
                </Alert>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </SoftBox>
  );
}

export default PublicRegistrationDemo;
